import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {
  DashboardOverviewStats,
  DetailedOverviewStats,
  EcuBuildStats,
  MasterExportStats,
  MasterExportTarget,
  MasterActivityPage,
  MasterTopUserUpdateStats,
  MasterTopUserUploadStats,
  PureOriginalExportStats,
  PureOriginalMatchTarget,
  PureOriginalMatchSort,
  PureOriginalMatchPage,
  PureOriginalExportTarget,
  PureOriginalActivityPage,
  PureOriginalExportTrendStats,
  PureOriginalTopUserStats,
  TopBuyerStats,
  TopBuyerTrendStats,
  TopEcuBuildStats,
  TopTuningPerEcuBuildStats,
  TrendGranularity,
  TrendPoint,
  TuningPopularityStats
} from '../models/dashboard-statistics.model';
import {NumberOfAllTunings} from '../../models/NumberOfAllTunings';
import {FileServiceRecordModel} from '../../models/FileServiceRecord-model';
import {PureOriginalsModel} from '../../models/PureOriginals-model';
import {AllMasterDatabaseModel} from '../../models/AllMasterDatabase-model';

export interface UploadedPureOriginalPage {
  content: PureOriginalsModel[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

export interface UploadedMastersPage {
  content: AllMasterDatabaseModel[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

export interface ImportsByTuningPage {
  content: FileServiceRecordModel[];
  number: number;
  size: number;
  last: boolean;
}

@Injectable({providedIn: 'root'})
export class DashboardStatisticsService {

  private readonly baseUrl: string;
  private readonly apiUrl: string;

  constructor(
    private http: HttpClient,
    @Inject('API_URL') apiUrl: string
  ) {
    this.apiUrl = apiUrl;
    this.baseUrl = `${apiUrl}/dashboard`;
  }

  // ── Overview ──────────────────────────────────────────────

  getOverview(startDate: string, endDate?: string): Observable<DashboardOverviewStats> {
    return this.http.get<DashboardOverviewStats>(`${this.baseUrl}/overview`, {
      params: this.dateParams(startDate, endDate)
    });
  }

  getDetailedOverview(startDate: string, endDate?: string, granularity: TrendGranularity = 'DAY'): Observable<DetailedOverviewStats[]> {
    return this.http.get<DetailedOverviewStats[]>(`${this.baseUrl}/overview/detailed`, {
      params: this.dateParams(startDate, endDate).set('granularity', granularity)
    });
  }

  // ── Imports ───────────────────────────────────────────────

  getTopTunings(startDate: string, endDate?: string, limit = 5): Observable<TuningPopularityStats[]> {
    return this.http.get<TuningPopularityStats[]>(`${this.baseUrl}/imports/top-tuning`, {
      params: this.dateParams(startDate, endDate).set('limit', limit)
    });
  }

  getImportsByTuning(tuningNames: string[], startDate: string, endDate: string, pageNumber?: number): Observable<ImportsByTuningPage> {
    let params = this.dateParams(startDate, endDate);
    for (const tuningName of tuningNames) {
      params = params.append('tuningNames', tuningName);
    }
    if (pageNumber !== undefined) {
      params = params.set('pageNumber', pageNumber);
    }
    return this.http.get<ImportsByTuningPage>(`${this.baseUrl}/imports/by-tuning`, {params});
  }

  getTopEcuBuilds(startDate: string, endDate?: string, limit = 5): Observable<TopEcuBuildStats[]> {
    return this.http.get<TopEcuBuildStats[]>(`${this.baseUrl}/imports/top-ecu-build`, {
      params: this.dateParams(startDate, endDate).set('limit', limit)
    });
  }

  getImportsTrend(startDate: string, endDate?: string, granularity: TrendGranularity = 'DAY'): Observable<TrendPoint[]> {
    return this.http.get<TrendPoint[]>(`${this.baseUrl}/imports/trend`, {
      params: this.dateParams(startDate, endDate).set('granularity', granularity)
    });
  }

  // ── Pure Original ─────────────────────────────────────────

  getPureOriginalMatches(target: PureOriginalMatchTarget, startDate: string, endDate?: string,
                         pageNumber = 0, pageSize = 50, sortAttribute: PureOriginalMatchSort = 'pureOriginalBaseId',
                         sortDirection: 'asc' | 'desc' = 'desc'): Observable<PureOriginalMatchPage> {
    return this.http.get<PureOriginalMatchPage>(`${this.baseUrl}/pure-original/${target}`, {
      params: this.dateParams(startDate, endDate)
        .set('pageNumber', Math.max(0, Math.floor(pageNumber)))
        .set('pageSize', Math.max(1, Math.min(100, Math.floor(pageSize))))
        .set('sortAttribute', sortAttribute)
        .set('sortDirection', sortDirection)
    });
  }

  getPopularTuningsByEcuBuild(startDate: string, endDate?: string, limit = 5): Observable<TopTuningPerEcuBuildStats[]> {
    return this.http.get<TopTuningPerEcuBuildStats[]>(`${this.baseUrl}/pure-original/popular-tunings-by-ecu-build`, {
      params: this.dateParams(startDate, endDate).set('limit', limit)
    });
  }

  getPopularStage1ByEcuBuild(startDate: string, endDate?: string, limit = 5): Observable<TopTuningPerEcuBuildStats[]> {
    return this.http.get<TopTuningPerEcuBuildStats[]>(`${this.baseUrl}/pure-original/popular-stage1-by-ecu-build`, {
      params: this.dateParams(startDate, endDate).set('limit', limit)
    });
  }

  getMissingMatchingByEcuBuild(limit?: number): Observable<EcuBuildStats[]> {
    const params = limit != null ? new HttpParams().set('limit', limit) : undefined;
    return this.http.get<EcuBuildStats[]>(`${this.baseUrl}/pure-original-missing/ecu-build`, {params});
  }

  getPartialMatchingByEcuBuild(limit?: number): Observable<EcuBuildStats[]> {
    const params = limit != null ? new HttpParams().set('limit', limit) : undefined;
    return this.http.get<EcuBuildStats[]>(`${this.baseUrl}/pure-original-partial/ecu-build`, {params});
  }

  getSuccessfulMatchingByEcuBuild(): Observable<EcuBuildStats[]> {
    return this.http.get<EcuBuildStats[]>(`${this.baseUrl}/pure-original-successful/ecu-build`);
  }

  getMissingAndPartialTotals(): Observable<NumberOfAllTunings> {
    // Not under /dashboard — hit the canonical pureOriginals endpoint directly.
    return this.http.get<NumberOfAllTunings>(`${this.apiUrl}/pureOriginals/numberOfMissingAndPartialTunings`);
  }

  getPureOriginalExportStats(startDate: string, endDate?: string): Observable<PureOriginalExportStats> {
    return this.http.get<PureOriginalExportStats>(`${this.baseUrl}/pure-original/export-stats`, {
      params: this.dateParams(startDate, endDate)
    });
  }

  getPureOriginalActivity(startDate: string, endDate: string, target: PureOriginalExportTarget,
                          pageNumber = 0, pageSize = 20): Observable<PureOriginalActivityPage> {
    return this.http.get<PureOriginalActivityPage>(`${this.baseUrl}/pure-original/exported`, {
      params: this.dateParams(startDate, endDate)
        .set('target', target)
        .set('pageNumber', pageNumber)
        .set('pageSize', pageSize)
    });
  }

  getPureOriginalExportTrends(startDate: string, endDate?: string, granularity: TrendGranularity = 'DAY'): Observable<PureOriginalExportTrendStats[]> {
    return this.http.get<PureOriginalExportTrendStats[]>(`${this.baseUrl}/pure-original/export-trends`, {
      params: this.dateParams(startDate, endDate).set('granularity', granularity)
    });
  }

  getUploadedPureOriginals(startDate: string, endDate: string | undefined, granularity: TrendGranularity, pageNumber = 0, pageSize = 50): Observable<UploadedPureOriginalPage> {
    return this.http.get<UploadedPureOriginalPage>(`${this.baseUrl}/pure-original/uploaded`, {
      params: this.dateParams(startDate, endDate)
        .set('granularity', granularity)
        .set('pageNumber', pageNumber)
        .set('pageSize', pageSize)
    });
  }

  getPureOriginalTrend(startDate: string, endDate?: string, granularity: TrendGranularity = 'DAY'): Observable<TrendPoint[]> {
    return this.http.get<TrendPoint[]>(`${this.baseUrl}/pure-original/trend`, {
      params: this.dateParams(startDate, endDate).set('granularity', granularity)
    });
  }

  getTopPureOriginalUsers(startDate: string, endDate?: string, granularity: TrendGranularity = 'DAY'): Observable<PureOriginalTopUserStats[]> {
    return this.http.get<PureOriginalTopUserStats[]>(`${this.baseUrl}/pure-original/top-users`, {
      params: this.dateParams(startDate, endDate).set('granularity', granularity)
    });
  }

  // ── Master ────────────────────────────────────────────────

  getUploadedMasters(startDate: string, endDate: string | undefined, granularity: TrendGranularity, pageNumber = 0, pageSize = 50): Observable<UploadedMastersPage> {
    return this.http.get<UploadedMastersPage>(`${this.baseUrl}/master/uploaded`, {
      params: this.dateParams(startDate, endDate)
        .set('granularity', granularity)
        .set('pageNumber', pageNumber)
        .set('pageSize', pageSize)
    });
  }

  getMasterExportStats(startDate: string, endDate?: string): Observable<MasterExportStats[]> {
    return this.http.get<MasterExportStats[]>(`${this.baseUrl}/master/export-stats`, {
      params: this.dateParams(startDate, endDate)
    });
  }

  getMasterActivity(startDate: string, endDate: string | undefined, target: MasterExportTarget,
                    pageNumber = 0, pageSize = 20): Observable<MasterActivityPage> {
    return this.http.get<MasterActivityPage>(`${this.baseUrl}/master/exported`, {
      params: this.dateParams(startDate, endDate)
        .set('target', target)
        .set('pageNumber', Math.max(0, Math.floor(pageNumber)))
        .set('pageSize', Math.max(1, Math.min(100, Math.floor(pageSize))))
    });
  }

  getMasterTrend(startDate: string, endDate?: string, granularity: TrendGranularity = 'DAY'): Observable<TrendPoint[]> {
    return this.http.get<TrendPoint[]>(`${this.baseUrl}/master/trend`, {
      params: this.dateParams(startDate, endDate).set('granularity', granularity)
    });
  }

  getTopMasterUploads(startDate: string, endDate?: string, granularity: TrendGranularity = 'DAY'): Observable<MasterTopUserUploadStats[]> {
    return this.http.get<MasterTopUserUploadStats[]>(`${this.baseUrl}/master/top-users-upload`, {
      params: this.dateParams(startDate, endDate).set('granularity', granularity)
    });
  }

  getTopMasterUpdates(startDate: string, endDate?: string, granularity: TrendGranularity = 'DAY'): Observable<MasterTopUserUpdateStats[]> {
    return this.http.get<MasterTopUserUpdateStats[]>(`${this.baseUrl}/master/top-users-update`, {
      params: this.dateParams(startDate, endDate).set('granularity', granularity)
    });
  }

  getVersionTrend(startDate: string, endDate?: string, granularity: TrendGranularity = 'DAY'): Observable<TrendPoint[]> {
    return this.http.get<TrendPoint[]>(`${this.baseUrl}/version/trend`, {
      params: this.dateParams(startDate, endDate).set('granularity', granularity)
    });
  }

  // ── WinOLS Transactions ───────────────────────────────────

  getTopBuyersTrend(startDate: string, endDate?: string, granularity: TrendGranularity = 'DAY'): Observable<TopBuyerTrendStats[]> {
    return this.http.get<TopBuyerTrendStats[]>(`${this.baseUrl}/win-ols-transactions/trends`, {
      params: this.dateParams(startDate, endDate).set('granularity', granularity)
    });
  }

  getTopBuyers(startDate: string, endDate?: string): Observable<TopBuyerStats[]> {
    return this.http.get<TopBuyerStats[]>(`${this.baseUrl}/win-ols-transactions/top-buyers`, {
      params: this.dateParams(startDate, endDate)
    });
  }

  // ── Helpers ───────────────────────────────────────────────

  private dateParams(startDate: string, endDate?: string): HttpParams {
    let params = new HttpParams().set('startDate', startDate);
    if (endDate) {
      params = params.set('endDate', endDate);
    }
    return params;
  }
}
